import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, Input, Button, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, Spinner, Badge } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import axiosInstance from "../../../helpers/axiosInstance";

const DTDCReport = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 30, total: 0, totalPages: 0 });
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState(""); // phone/order/user search
  const [pincode, setPincode] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [summary, setSummary] = useState({ count: 0, amount: 0, deliveryCharge: 0 });

  const loadData = async () => {
    try {
      if (!initialLoading) setRefreshing(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (status) params.status = status;
      if (search) params.search = search;
      if (pincode) params.pincode = pincode;
      if (minAmount) params.minAmount = minAmount;
      if (from) params.from = from;
      if (to) params.to = to;

      const { data } = await axiosInstance.get("api/v1/admin/reports/dtdc-cod", { params });
      // Expecting shape: { success, filters, summary, pagination, orders }
      setItems(data?.orders || data?.data || data?.items || []);
      setSummary({
        count: Number(data?.summary?.count || 0),
        amount: Number(data?.summary?.amount || 0),
        deliveryCharge: Number(data?.summary?.deliveryCharge || 0),
      });
      const serverPagination = data?.pagination || {};
      setPagination((prev) => ({
        ...prev,
        page: serverPagination.page || prev.page,
        limit: serverPagination.limit || prev.limit,
        total: serverPagination.total || serverPagination.totalOrders || prev.total || 0,
        totalPages:
          serverPagination.totalPages ||
          (serverPagination.total ? Math.ceil(serverPagination.total / (serverPagination.limit || prev.limit)) : prev.totalPages) ||
          0,
      }));
    } catch (e) {
      console.error("Failed to load DTDC COD report", e);
      setItems([]);
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, status, search, pincode, minAmount, from, to]);

  const handlePageChange = (page) => setPagination((p) => ({ ...p, page }));

  const getStatusColor = (s) => {
    switch ((s || "").toLowerCase()) {
      case "delivered":
        return "success";
      case "outfordelivery":
        return "info";
      case "intransit":
      case "shipped":
        return "info";
      case "pending":
        return "warning";
      case "cancelled":
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getCommonParams = () => {
    const params = {
      page: pagination.page,
      limit: pagination.limit,
    };
    if (status) params.status = status;
    if (search) params.search = search;
    if (pincode) params.pincode = pincode;
    if (minAmount) params.minAmount = minAmount;
    if (from) params.from = from;
    if (to) params.to = to;
    return params;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="DTDC COD Report" breadcrumbItems={[{ title: "Reports", link: "#" }, { title: "DTDC COD", link: "#" }]} />

        <Row>
          {/* Summary */}
          <Col xs={12} className="mb-3">
            <div className="d-flex gap-3 flex-wrap">
              <Card className="flex-grow-1" style={{ minWidth: 220 }}>
                <CardBody className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted">Total Orders</div>
                    <div className="fs-4 fw-semibold">{summary.count}</div>
                  </div>
                  <i className="ri-file-list-3-line fs-3 text-primary"></i>
                </CardBody>
              </Card>
              <Card className="flex-grow-1" style={{ minWidth: 220 }}>
                <CardBody className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted">Total Amount</div>
                    <div className="fs-4 fw-semibold">₹{Number(summary.amount || 0).toLocaleString("en-IN")}</div>
                  </div>
                  <i className="ri-money-rupee-circle-line fs-3 text-success"></i>
                </CardBody>
              </Card>
              <Card className="flex-grow-1" style={{ minWidth: 220 }}>
                <CardBody className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted">Delivery Charges</div>
                    <div className="fs-4 fw-semibold">₹{Number(summary.deliveryCharge || 0).toLocaleString("en-IN")}</div>
                  </div>
                  <i className="ri-truck-line fs-3 text-warning"></i>
                </CardBody>
              </Card>
            </div>
          </Col>
          <Col xs={12}>
            <Card>
              <CardBody>
                <div className="d-flex flex-wrap gap-3 align-items-center mb-4">
                  <UncontrolledDropdown>
                    <DropdownToggle caret color="primary" outline className="px-4 d-flex align-items-center">
                      <i className="ri-filter-3-line me-2"></i>
                      {status ? status : "All Status"}
                    </DropdownToggle>
                    <DropdownMenu className="shadow-lg">
                      <DropdownItem active={status === ""} onClick={() => setStatus("")}>All Status</DropdownItem>
                      <DropdownItem active={status === "delivered"} onClick={() => setStatus("delivered")}>Delivered</DropdownItem>
                      <DropdownItem active={status === "pending"} onClick={() => setStatus("pending")}>Pending</DropdownItem>
                      <DropdownItem active={status === "intransit"} onClick={() => setStatus("intransit")}>In Transit</DropdownItem>
                      <DropdownItem active={status === "outForDelivery"} onClick={() => setStatus("outForDelivery")}>Out For Delivery</DropdownItem>
                      <DropdownItem active={status === "cancelled"} onClick={() => setStatus("cancelled")}>Cancelled</DropdownItem>
                      <DropdownItem active={status === "rejected"} onClick={() => setStatus("rejected")}>Rejected</DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>

                  <div className="d-flex gap-2 align-items-center">
                    <Input placeholder="Search phone/order..." value={search} onChange={(e) => { setSearch(e.target.value); setPagination((p)=>({ ...p, page: 1 })); }} style={{ minWidth: "200px" }} />
                    <Input placeholder="Pincode" value={pincode} onChange={(e) => { setPincode(e.target.value); setPagination((p)=>({ ...p, page: 1 })); }} style={{ width: "140px" }} />
                    <Input placeholder="Min Amount" type="number" value={minAmount} onChange={(e) => { setMinAmount(e.target.value); setPagination((p)=>({ ...p, page: 1 })); }} style={{ width: "140px" }} />
                  </div>

                  <div className="d-flex gap-2 align-items-center">
                    <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPagination((p)=>({ ...p, page: 1 })); }} />
                    <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPagination((p)=>({ ...p, page: 1 })); }} />
                  </div>

                  <div className="ms-auto d-flex align-items-center gap-2">
                    <UncontrolledDropdown>
                      <DropdownToggle caret color="success" className="px-3 d-flex align-items-center" disabled={exporting || initialLoading}>
                        <i className="ri-download-2-line me-2"></i>
                        {exporting ? "Exporting..." : "Export"}
                      </DropdownToggle>
                      <DropdownMenu className="shadow-lg">
                        <DropdownItem disabled={exporting} onClick={async () => {
                          try {
                            setExporting(true);
                            // Export current page items only
                            const rows = items;
                            const headers = [
                              "Order ID",
                              "Customer",
                              "Phone",
                              "Pincode",
                              "City",
                              "State",
                              "Amount",
                              "Status",
                              "DTDC Ref",
                              "Date",
                            ];
                            const escapeCsv = (val) => {
                              const s = (val === null || val === undefined) ? "" : String(val);
                              if (/[",\n]/.test(s)) {
                                return '"' + s.replace(/"/g, '""') + '"';
                              }
                              return s;
                            };
                            const mapRow = (it) => {
                              const id = it?.id || it?._id || it?.orderId || it?.order_id;
                              const name = it?.customerName || it?.shippingAddress?.name || it?.user?.name || "-";
                              const phone = it?.phone || it?.user?.phone || it?.shippingAddress?.phone || "-";
                              const pin = it?.pincode || it?.shippingAddress?.postalCode || "-";
                              const city = it?.city || it?.shippingAddress?.city || "-";
                              const state = it?.state || it?.shippingAddress?.state || "-";
                              const amt = it?.totalAmount ?? it?.amount ?? 0;
                              const st = it?.status || it?.dtdcStatus || "-";
                              const ref = it?.dtdcTrackingCode || it?.reference_number || "-";
                              const date = it?.createdAt || it?.dispatchDate || it?.date;
                              return [
                                id,
                                name,
                                phone,
                                pin,
                                city,
                                state,
                                Number(amt || 0).toFixed(0),
                                st,
                                ref,
                                date ? new Date(date).toLocaleString("en-IN") : "-",
                              ];
                            };
                            const csv = [headers, ...rows.map(mapRow)]
                              .map(r => r.map(escapeCsv).join(","))
                              .join("\n");
                            const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            const ts = new Date();
                            const pad = (n) => String(n).padStart(2, "0");
                            const fname = `dtdc-cod-report-page-${ts.getFullYear()}${pad(ts.getMonth()+1)}${pad(ts.getDate())}-${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}.csv`;
                            a.download = fname;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          } catch (e) {
                            console.error("Export failed", e);
                          } finally {
                            setExporting(false);
                          }
                        }}>Export current page (CSV)</DropdownItem>
                        <DropdownItem disabled={exporting} onClick={async () => {
                          try {
                            setExporting(true);
                            // Fetch all filtered data in batches and export
                            const headers = [
                              "Order ID",
                              "Customer",
                              "Phone",
                              "Pincode",
                              "City",
                              "State",
                              "Amount",
                              "Status",
                              "DTDC Ref",
                              "Date",
                            ];
                            const escapeCsv = (val) => {
                              const s = (val === null || val === undefined) ? "" : String(val);
                              if (/[",\n]/.test(s)) {
                                return '"' + s.replace(/"/g, '""') + '"';
                              }
                              return s;
                            };
                            const mapRow = (it) => {
                              const id = it?.id || it?._id || it?.orderId || it?.order_id;
                              const name = it?.customerName || it?.shippingAddress?.name || it?.user?.name || "-";
                              const phone = it?.phone || it?.user?.phone || it?.shippingAddress?.phone || "-";
                              const pin = it?.pincode || it?.shippingAddress?.postalCode || "-";
                              const city = it?.city || it?.shippingAddress?.city || "-";
                              const state = it?.state || it?.shippingAddress?.state || "-";
                              const amt = it?.totalAmount ?? it?.amount ?? 0;
                              const st = it?.status || it?.dtdcStatus || "-";
                              const ref = it?.dtdcTrackingCode || it?.reference_number || "-";
                              const date = it?.createdAt || it?.dispatchDate || it?.date;
                              return [
                                id,
                                name,
                                phone,
                                pin,
                                city,
                                state,
                                Number(amt || 0).toFixed(0),
                                st,
                                ref,
                                date ? new Date(date).toLocaleString("en-IN") : "-",
                              ];
                            };

                            const rows = [];
                            const baseParams = getCommonParams();
                            const exportLimit = 1000; // try to reduce number of requests
                            let currentPage = 1;
                            let totalPagesToFetch = 1;
                            do {
                              const params = { ...baseParams, page: currentPage, limit: exportLimit };
                              const { data } = await axiosInstance.get("api/v1/admin/reports/dtdc-cod", { params });
                              const pageItems = data?.orders || data?.data || data?.items || [];
                              rows.push(...pageItems);
                              const serverPagination = data?.pagination || {};
                              const total = serverPagination.total || serverPagination.totalOrders || rows.length;
                              const totalPages = serverPagination.totalPages || (total ? Math.ceil(total / exportLimit) : currentPage);
                              totalPagesToFetch = totalPages;
                              currentPage += 1;
                            } while (currentPage <= totalPagesToFetch);

                            const csv = [headers, ...rows.map(mapRow)]
                              .map(r => r.map(escapeCsv).join(","))
                              .join("\n");
                            const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            const ts = new Date();
                            const pad = (n) => String(n).padStart(2, "0");
                            const fname = `dtdc-cod-report-all-${ts.getFullYear()}${pad(ts.getMonth()+1)}${pad(ts.getDate())}-${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}.csv`;
                            a.download = fname;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          } catch (e) {
                            console.error("Export failed", e);
                          } finally {
                            setExporting(false);
                          }
                        }}>Export all (CSV)</DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                    <UncontrolledDropdown>
                      <DropdownToggle caret color="light" outline className="px-3 d-flex align-items-center shadow-none" style={{ borderRadius: "6px", minWidth: "90px" }}>
                        {pagination.limit} Rows
                      </DropdownToggle>
                      <DropdownMenu className="shadow-lg" style={{ minWidth: "90px" }}>
                        {[10, 25, 50, 100].map((pageSize) => (
                          <DropdownItem key={pageSize} onClick={() => setPagination((prev) => ({ ...prev, limit: pageSize, page: 1 }))} active={pagination.limit === pageSize} className="d-flex align-items-center justify-content-center">
                            {pageSize} Rows
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                    {refreshing && <Spinner size="sm" color="primary" />}
                  </div>
                </div>

                <div className="table-responsive">
                  {initialLoading ? (
                    <div className="text-center my-5"><Spinner color="primary" /></div>
                  ) : (
                    <table className="table align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Order ID</th>
                          <th>Customer</th>
                          <th>Phone</th>
                          <th>Pincode</th>
                          <th>City</th>
                          <th>State</th>
                          <th>Amount</th>
                          <th>Status</th>
                          <th>DTDC Ref</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.length === 0 && (
                          <tr>
                            <td colSpan={8} className="text-center text-muted py-4">No data found</td>
                          </tr>
                        )}
                        {items.map((it) => {
                          const id = it?.id || it?._id || it?.orderId || it?.order_id;
                          const name = it?.customerName || it?.shippingAddress?.name || it?.user?.name || "-";
                          const phone = it?.phone || it?.user?.phone || it?.shippingAddress?.phone || "-";
                          const pin = it?.pincode || it?.shippingAddress?.postalCode || "-";
                          const city = it?.city || it?.shippingAddress?.city || "-";
                          const state = it?.state || it?.shippingAddress?.state || "-";
                          const amt = it?.totalAmount ?? it?.amount ?? 0;
                          const st = it?.status || it?.dtdcStatus || "-";
                          const ref = it?.dtdcTrackingCode || it?.reference_number || "-";
                          const date = it?.createdAt || it?.dispatchDate || it?.date;
                          return (
                            <tr key={`${id}-${ref}`}>
                              <td>{id}</td>
                              <td>{name}</td>
                              <td>{phone}</td>
                              <td>{pin}</td>
                              <td>{city}</td>
                              <td>{state}</td>
                              <td>₹{Number(amt || 0).toFixed(0)}</td>
                              <td>
                                <Badge color={getStatusColor(st)} pill>{(st || "").toString()}</Badge>
                              </td>
                              <td>{ref}</td>
                              <td>{date ? new Date(date).toLocaleString("en-IN") : "-"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                {!initialLoading && items.length > 0 && (
                  <div className="d-flex align-items-center justify-content-between mt-3">
                    <span className="text-muted">
                      Showing {(pagination.page - 1) * pagination.limit + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                    </span>
                    <div className="d-flex gap-2">
                      <Button disabled={pagination.page <= 1} onClick={() => handlePageChange(pagination.page - 1)} color="light">Prev</Button>
                      <Button disabled={pagination.page >= (pagination.totalPages || 1)} onClick={() => handlePageChange(pagination.page + 1)} color="light">Next</Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DTDCReport;


